import { cheaper } from './prices.ts';
import { hourOf, progress, spend, type LifeEvent, type LifeState, money } from './life.ts';
import { nudge, traitsOf } from './character.ts';

/**
 * Days out: places you go for no reason except that they're good. The first is the Lekki
 * Conservation Centre (mangrove forest, a boardwalk over the swamp, the canopy walkway, monkeys,
 * peacocks and tortoises on the lawn). The game lets you walk it; this decides what it costs, when
 * it's open, and what each moment does to you. A moment counts once per visit.
 */

const clamp = (v: number) => Math.max(0, Math.min(100, v));
const naira = (s: LifeState, n: number) => money(s, n);

export const LCC = {
  name: 'Lekki Conservation Centre',
  area: 'Lekki',
  entry: cheaper(3_000),
  canopy: cheaper(5_000),
  /** 8:30 to 17:00 every day; last entry at 16:00 */
  opens: 8.5,
  closes: 17,
  lastEntry: 16,
} as const;

export type ParkMoment = 'peacock' | 'peacock_fan' | 'monkey' | 'monkey_snatch' | 'tortoise' | 'crocodile' | 'lookout' | 'canopy' | 'treehouse' | 'picnic';

export interface OutingState { day: number; place: string; seen: string[]; canopyPaid: boolean }

export function outingOf(s: LifeState): OutingState | null {
  const o = (s as LifeState & { outing?: OutingState }).outing ?? null;
  return o && o.day === Math.floor(s.minutes / 1440) ? o : null;
}

export function parkOpen(s: LifeState): { open: boolean; line: string } {
  const h = hourOf(s.minutes);
  if (h < LCC.opens) return { open: false, line: `The gate is shut. They open at 8:30.` };
  if (h >= LCC.lastEntry) return { open: false, line: h >= LCC.closes ? 'Closed for the day. The rangers are locking up.' : 'Last entry was at 4:00. The ranger shakes his head.' };
  return { open: true, line: '' };
}

/** At the gate: pay the entry fee (once a day) and start the visit. */
export function enterPark(s: LifeState): { ok: boolean; line: string } {
  const o = outingOf(s);
  if (o && o.place === LCC.name) return { ok: true, line: 'You show your wristband. In you go.' };
  const g = parkOpen(s);
  if (!g.open) return { ok: false, line: g.line };
  if (!spend(s, LCC.entry, `Entry: ${LCC.name}`)) return { ok: false, line: `Entry is ${naira(s, LCC.entry)}. You don't have it on you.` };
  (s as LifeState & { outing?: OutingState }).outing = { day: Math.floor(s.minutes / 1440), place: LCC.name, seen: [], canopyPaid: false };
  return { ok: true, line: `${naira(s, LCC.entry)} at the gate. A ranger ties a paper wristband on you: "Don't feed the monkeys. They will collect it anyway."` };
}

/** The canopy walkway has its own ticket. */
export function payCanopy(s: LifeState): { ok: boolean; line: string } {
  const o = outingOf(s);
  if (!o) return { ok: false, line: 'Buy an entry ticket at the gate first.' };
  if (o.canopyPaid) return { ok: true, line: '' };
  if (!spend(s, LCC.canopy, 'Canopy walkway')) return { ok: false, line: `The canopy walk is ${naira(s, LCC.canopy)}. Not today.` };
  o.canopyPaid = true;
  const nervous = traitsOf(s).includes('cautious');
  return { ok: true, line: nervous ? `${naira(s, LCC.canopy)}. You look up at the first tower. It's very high. You go anyway.` : `${naira(s, LCC.canopy)}. Up you go.` };
}

const MOMENT: Record<ParkMoment, { fun: number; social?: number; energy?: number; line: string }> = {
  peacock: { fun: 3, line: 'A peacock struts past like it owns the place. It does.' },
  peacock_fan: { fun: 10, line: 'You stood still long enough: the peacock fans its tail, a hundred eyes shivering. Everyone nearby gets their phone out.' },
  monkey: { fun: 6, line: 'A Mona monkey watches you from the rail, then hops off into the trees.' },
  monkey_snatch: { fun: 8, line: 'A monkey drops onto the rail and snatches the snack out of your hand. Gone. The family behind you can\'t stop laughing.' },
  tortoise: { fun: 5, line: 'The tortoise is older than your parents. It eats a leaf very, very slowly.' },
  crocodile: { fun: 6, line: 'A crocodile lies on the bank with its mouth open, perfectly still. You step back from the fence anyway.' },
  lookout: { fun: 6, energy: 4, line: 'The swamp lookout: raffia roof, birds you can\'t name, the city very far away.' },
  canopy: { fun: 25, social: 6, energy: -8, line: 'You walked the whole canopy walkway, swaying above the forest. Your legs are still shaking a little. Worth it.' },
  treehouse: { fun: 8, energy: -3, line: 'From the treehouse you can see the whole forest canopy, and the lagoon beyond it.' },
  picnic: { fun: 6, social: 4, line: 'You sit at a picnic table in the shade. A peacock comes to inspect your food.' },
};

/** Something happened on the visit: counts once (the first peacock fan, the whole canopy walk). */
export function parkMoment(s: LifeState, m: ParkMoment): { line: string; events: LifeEvent[] } | null {
  const o = outingOf(s);
  if (!o || o.seen.includes(m)) return null;
  o.seen.push(m);
  const d = MOMENT[m];
  s.needs.fun = clamp(s.needs.fun + d.fun);
  if (d.social) s.needs.social = clamp(s.needs.social + d.social);
  if (d.energy) s.needs.energy = clamp(s.needs.energy + d.energy);
  const events: LifeEvent[] = [];
  if (m === 'canopy') {
    s.history.push({ t: s.minutes, text: `Walked the canopy walkway at ${LCC.name}.` });
    // facing it down moves you a little: the cautious learn they can, the reckless loved it
    nudge(s, 'reckless', traitsOf(s).includes('cautious') ? 0.06 : 0.03, events);
  }
  if (o.seen.length === 3) events.push(...progress(s, 'outing'));
  return { line: d.line, events };
}

/** Leaving: a line about the day, depending on how much of it you did. */
export function leavePark(s: LifeState): string {
  const o = outingOf(s);
  if (!o) return '';
  const n = o.seen.length;
  return n >= 5 ? 'A proper day out. You\'ll be showing people the photos for a week.' : n >= 2 ? 'A good few hours away from everything.' : 'You barely saw any of it. Next time, go further in.';
}
